/**
 * useMediaQuery — subscribe to a CSS media query and re-render on match changes.
 *
 * SSR/test-safe: returns `false` when `window.matchMedia` is unavailable.
 * Used by the V5 workbench to serve the mobile shell below ~768px (FXC-8939).
 */

import { useEffect, useState } from "react";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    // Sync immediately in case the query changed between render and effect.
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * The V5 mobile-shell breakpoint, in CSS px — aligned with the share viewer
 * (FXC-8724). This is the single source of truth for "are we in the mobile
 * shell": the matchMedia query below is derived from it, and every mobile
 * media query in the V5 stylesheets MUST use the same value (FXC-8998).
 *
 * Previously the JS mounted the mobile shell at <=768px while the CSS only
 * switched at <=760px, leaving a 761–768px dead zone (notably iPad portrait,
 * 768px) where the mobile shell rendered with desktop CSS. Keep these in lock
 * step: if you change this number, update the `@media (max-width: 768px)`
 * blocks in `features/thread-v5/thread-v5.css` and `thread-v5-mobile.css` too.
 */
export const V5_MOBILE_BREAKPOINT_PX = 768;

/** The V5 mobile-shell breakpoint media query, derived from the px constant. */
export const V5_MOBILE_QUERY = `(max-width: ${V5_MOBILE_BREAKPOINT_PX}px)`;

export function useIsV5Mobile(): boolean {
  return useMediaQuery(V5_MOBILE_QUERY);
}
